/**
 * Furnace block entity (vanilla AbstractFurnaceBlockEntity.serverTick rules).
 * DOM-free and serializable; keys are "x,y,z".
 *
 *   state.tick()        one 20 Hz tick; true when `lit` flipped
 *   world.get(key)      state for a furnace block (created on first use)
 *   world.tick(cb)      tick every furnace, cb(key, lit) on lit changes
 *   serialize()/load()  flat number arrays: [in(3) fuel(3) out(3) burnLeft burnTotal cook]
 */

import { clearStack, emptyStack, isEmpty, type Stack } from './types';
import { maxStackOf } from './itemdb';
import { I } from './itemIds';
import { SMELT_TICKS, fuelTicks, smeltResult, smeltXp } from './smelting';

export class FurnaceState {
  input: Stack = emptyStack();
  fuel: Stack = emptyStack();
  output: Stack = emptyStack();
  /** Ticks of burn time left on the current fuel item. */
  burnLeft = 0;
  /** Burn time of the fuel item that was last consumed (flame animation denominator). */
  burnTotal = 0;
  /** 0..SMELT_TICKS progress of the current item. */
  cookProgress = 0;
  /** Experience stored by smelts whose output is still in the slot, and how many items it covers. */
  xp = 0;
  xpItems = 0;

  /** Experience owed for taking `n` items out of the output slot (vanilla pays on removal). */
  claimXp(n: number): number {
    if (this.xpItems <= 0) return 0;
    const k = Math.min(n, this.xpItems);
    const amt = (this.xp * k) / this.xpItems;
    this.xp -= amt; this.xpItems -= k;
    if (this.xpItems <= 0) this.xp = this.xpItems = 0;
    return amt;
  }

  get lit(): boolean { return this.burnLeft > 0; }

  /** Can the current input be smelted into the output slot right now? */
  private canSmelt(): boolean {
    if (isEmpty(this.input)) return false;
    const r = smeltResult(this.input.id);
    if (!r) return false;
    if (isEmpty(this.output)) return true;
    return this.output.id === r.id && this.output.dmg === r.dmg &&
      this.output.count + r.count <= maxStackOf(r.id);
  }

  tick(): boolean {
    const wasLit = this.lit;
    if (wasLit) this.burnLeft--;
    if (this.lit || (!isEmpty(this.fuel) && !isEmpty(this.input))) {
      const can = this.canSmelt();
      if (!this.lit && can) {
        const t = fuelTicks(this.fuel.id);
        if (t > 0 && !isEmpty(this.fuel)) {
          this.burnLeft = this.burnTotal = t;
          const fid = this.fuel.id;
          this.fuel.count--;
          if (this.fuel.count <= 0) clearStack(this.fuel);
          // a burnt lava bucket leaves its empty bucket behind (vanilla)
          if (fid === I.LAVA_BUCKET && isEmpty(this.fuel)) { this.fuel.id = I.BUCKET; this.fuel.count = 1; this.fuel.dmg = 0; }
        }
      }
      if (this.lit && can) {
        if (++this.cookProgress >= SMELT_TICKS) {
          this.cookProgress = 0;
          const r = smeltResult(this.input.id)!;
          if (isEmpty(this.output)) { this.output.id = r.id; this.output.count = r.count; this.output.dmg = r.dmg; }
          else this.output.count += r.count;
          this.xp += smeltXp(this.input.id); this.xpItems += r.count;
          this.input.count--;
          if (this.input.count <= 0) clearStack(this.input);
        }
      } else this.cookProgress = 0;
    } else if (this.cookProgress > 0) {
      this.cookProgress = Math.max(0, this.cookProgress - 2);
    }
    return wasLit !== this.lit;
  }

  /** True when there is nothing in any slot and nothing in progress (not worth saving). */
  get isIdleEmpty(): boolean {
    return isEmpty(this.input) && isEmpty(this.fuel) && isEmpty(this.output) &&
      this.burnLeft === 0 && this.cookProgress === 0 && this.xpItems === 0;
  }

  serialize(): number[] {
    const s = (x: Stack): number[] => (isEmpty(x) ? [0, 0, 0] : [x.id, x.count, x.dmg]);
    return [...s(this.input), ...s(this.fuel), ...s(this.output), this.burnLeft, this.burnTotal, this.cookProgress, this.xp, this.xpItems];
  }

  static from(flat?: number[]): FurnaceState {
    const f = new FurnaceState();
    if (!flat || flat.length < 12) return f;
    const n = (i: number): number => (Number.isFinite(flat[i]) ? flat[i] : 0);
    const put = (dst: Stack, o: number): void => {
      dst.id = n(o); dst.count = n(o + 1); dst.dmg = n(o + 2);
      if (isEmpty(dst)) clearStack(dst);
    };
    put(f.input, 0); put(f.fuel, 3); put(f.output, 6);
    f.burnLeft = Math.max(0, n(9));
    f.burnTotal = Math.max(0, n(10));
    f.cookProgress = Math.max(0, n(11));
    f.xp = Math.max(0, n(12)); f.xpItems = Math.max(0, n(13)); // absent in older saves
    return f;
  }
}

export class FurnaceWorld {
  private readonly map = new Map<string, FurnaceState>();

  get(key: string): FurnaceState {
    let s = this.map.get(key);
    if (!s) { s = new FurnaceState(); this.map.set(key, s); }
    return s;
  }
  entries(): IterableIterator<[string, FurnaceState]> { return this.map.entries(); }
  peek(key: string): FurnaceState | undefined { return this.map.get(key); }
  remove(key: string): FurnaceState | undefined {
    const s = this.map.get(key);
    this.map.delete(key);
    return s;
  }

  tick(onLitChange: (key: string, lit: boolean) => void): void {
    for (const [key, s] of this.map) {
      if (s.tick()) onLitChange(key, s.lit);
    }
  }

  serialize(): Record<string, number[]> {
    const out: Record<string, number[]> = {};
    for (const [k, s] of this.map) if (!s.isIdleEmpty) out[k] = s.serialize();
    return out;
  }

  load(r: Record<string, number[]>): void {
    this.map.clear();
    for (const k of Object.keys(r)) this.map.set(k, FurnaceState.from(r[k]));
  }
}
